import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Calendar, MapPin, Play, ChevronRight, Plus, Search,
  Users, Clock, Radio, Upload, Download, FileSpreadsheet,
  Loader2, CheckCircle2, XCircle, Edit, Trash2, MoreVertical, Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge colors
const statusColors = {
  scheduled: 'bg-zinc-600',
  warmup: 'bg-yellow-600',
  live: 'bg-red-600 animate-pulse',
  halftime: 'bg-orange-600',
  final: 'bg-green-600'
};

export default function OperatorSelect() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  
  const [games, setGames] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterEventId, setFilterEventId] = useState('all');
  const [filterCourtId, setFilterCourtId] = useState('all');
  
  // Dialog states
  const [showCreateGameDialog, setShowCreateGameDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [showCreateEventDialog, setShowCreateEventDialog] = useState(false);
  const [showEditGameDialog, setShowEditGameDialog] = useState(false);
  const [showEditEventDialog, setShowEditEventDialog] = useState(false);
  const [editingGame, setEditingGame] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showRosterDialog, setShowRosterDialog] = useState(false);
  const [rosterTeamName, setRosterTeamName] = useState('');
  const [rosterText, setRosterText] = useState('');
  const [rosterSaving, setRosterSaving] = useState(false);
  const [rosterSide, setRosterSide] = useState('home'); // 'home' or 'away'
  const [rosterGameId, setRosterGameId] = useState(null);
  
  // Form states
  const [gameForm, setGameForm] = useState({
    title: '',
    home_team_name: '',
    away_team_name: '',
    sport: 'basketball',
    location: '',
    event_id: ''
  });
  const [eventForm, setEventForm] = useState({
    name: '',
    sport: 'basketball',
    location: '',
    date: '',
    end_date: '',
    description: ''
  });
  const [bulkImportFile, setBulkImportFile] = useState(null);
  const [bulkImportEventId, setBulkImportEventId] = useState('');
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);

  // Fetch games
  const fetchGames = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/engine/games?limit=200`);
      const data = await res.json();
      setGames(data);
    } catch (err) {
      toast.error('Failed to load games');
    }
  }, []);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/events`);
      const data = await res.json();
      setEvents(data);
    } catch (err) {
      console.error('Failed to load events');
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchGames(), fetchEvents()]);
      setLoading(false);
    };
    load();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchGames, 30000);
    return () => clearInterval(interval);
  }, [fetchGames, fetchEvents]);

  // Create new game with form data
  const createGame = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    
    if (!gameForm.home_team_name || !gameForm.away_team_name) {
      toast.error('Please enter both team names');
      return;
    }
    
    try {
      const payload = {
        home_team_name: gameForm.home_team_name,
        away_team_name: gameForm.away_team_name,
        sport: gameForm.sport,
        title: gameForm.title || `${gameForm.home_team_name} vs ${gameForm.away_team_name}`,
        location: gameForm.location
      };
      if (gameForm.event_id) {
        payload.event_id = gameForm.event_id;
      }
      
      const res = await fetch(`${API_URL}/api/engine/games`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const data = await res.json();
      if (data.success) {
        toast.success('Game created');
        setShowCreateGameDialog(false);
        setGameForm({ title: '', home_team_name: '', away_team_name: '', sport: 'basketball', location: '', event_id: '' });
        navigate(`/operator/game/${data.game.game_id}`);
      } else {
        toast.error(data.detail || 'Failed to create game');
      }
    } catch (err) {
      toast.error('Failed to create game');
    }
  };
  
  // Create new event
  const createEvent = async () => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    
    if (!eventForm.name || !eventForm.location || !eventForm.date) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventForm)
      });
      
      if (res.ok) {
        toast.success('Event created successfully!');
        setShowCreateEventDialog(false);
        setEventForm({ name: '', sport: 'basketball', location: '', date: '', end_date: '', description: '' });
        fetchEvents();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create event');
      }
    } catch (err) {
      toast.error('Failed to create event');
    }
  };
  
  // Bulk import games
  const handleBulkImport = async () => {
    if (!bulkImportFile) {
      toast.error('Please select a file to import');
      return;
    }
    setBulkImporting(true);
    setBulkImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkImportFile);
      const url = `${API_URL}/api/engine/games/bulk-import${bulkImportEventId ? `?event_id=${bulkImportEventId}` : ''}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (res.ok) {
        setBulkImportResult(data);
        if (data.imported > 0) {
          toast.success(`Successfully imported ${data.imported} games!`);
          fetchGames();
        }
      } else {
        toast.error(data.detail || 'Import failed');
        setBulkImportResult({ success: false, imported: 0, failed: 1, errors: [data.detail || 'Unknown error'] });
      }
    } catch (err) {
      toast.error('Failed to import games');
      setBulkImportResult({ success: false, imported: 0, failed: 1, errors: [err.message] });
    }
    setBulkImporting(false);
  };

  // Edit game - open dialog with game data
  const openEditGame = (game) => {
    setEditingGame(game);
    setGameForm({
      title: game.title || '',
      home_team_name: game.home_team_name || '',
      away_team_name: game.away_team_name || '',
      sport: game.sport || 'basketball',
      location: game.location || '',
      event_id: game.event_id || ''
    });
    setShowEditGameDialog(true);
  };

  // Update game
  const updateGame = async () => {
    if (!token) {
      toast.error('Please log in to update games');
      return;
    }
    
    if (!editingGame) {
      toast.error('No game selected for editing');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${editingGame.game_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: gameForm.title || `${gameForm.home_team_name} vs ${gameForm.away_team_name}`,
          home_team_name: gameForm.home_team_name,
          away_team_name: gameForm.away_team_name,
          sport: gameForm.sport,
          location: gameForm.location,
          event_id: gameForm.event_id || null
        })
      });
      
      if (res.ok) {
        toast.success('Game updated successfully');
        setShowEditGameDialog(false);
        setEditingGame(null);
        fetchGames();
      } else {
        const err = await res.json();
        if (res.status === 401) {
          toast.error('Session expired. Please log in again.');
        } else {
          toast.error(err.detail || 'Failed to update game');
        }
      }
    } catch (err) {
      toast.error('Failed to update game');
    }
  };

  // Reset game status (revert from live/final back to scheduled)
  const resetGameStatus = async (gameId, newStatus = 'scheduled') => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Game status reset to ${newStatus}`);
        fetchGames();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to reset game status');
      }
    } catch (err) {
      toast.error('Failed to reset game status');
    }
  };

  // Delete game
  const deleteGame = async (gameId, gameTitle) => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${gameTitle}"?`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/engine/games/${gameId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Game deleted');
        fetchGames();
      } else {
        toast.error('Failed to delete game');
      }
    } catch (err) {
      toast.error('Failed to delete game');
    }
  };

  // Edit event - open dialog with event data
  const openEditEvent = (event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name || '',
      sport: event.sport || 'basketball',
      location: event.location || '',
      date: event.date || '',
      end_date: event.end_date || '',
      description: event.description || ''
    });
    setShowEditEventDialog(true);
  };

  // Update event
  const updateEvent = async () => {
    if (!token || !editingEvent) {
      toast.error('Authentication required');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventForm)
      });
      
      if (res.ok) {
        toast.success('Event updated successfully');
        setShowEditEventDialog(false);
        setEditingEvent(null);
        fetchEvents();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update event');
      }
    } catch (err) {
      toast.error('Failed to update event');
    }
  };

  // Delete event
  const deleteEvent = async (eventId, eventName) => {
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete "${eventName}"? This will NOT delete associated games.`)) {
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Event deleted');
        fetchEvents();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (err) {
      toast.error('Failed to delete event');
    }
  };

  // Roster management
  const openRosterDialog = async (teamName, side, gameId) => {
    setRosterTeamName(teamName);
    setRosterSide(side);
    setRosterGameId(gameId);
    setRosterSaving(false);
    // Load existing roster
    try {
      const res = await fetch(`${API_URL}/api/teams/by-name/${encodeURIComponent(teamName)}`);
      const data = await res.json();
      if (data.players && data.players.length > 0) {
        setRosterText(data.players.map(p => `${p.jersey_number || 0} ${p.name}`).join('\n'));
      } else {
        setRosterText('');
      }
    } catch {
      setRosterText('');
    }
    setShowRosterDialog(true);
  };

  const saveRoster = async () => {
    if (!rosterText.trim()) {
      toast.error('Enter at least one player');
      return;
    }
    setRosterSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/teams/roster/bulk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ team_name: rosterTeamName, roster_text: rosterText }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Roster saved — ${data.players_added} players for ${rosterTeamName}`);
        if (data.errors?.length > 0) {
          toast.warning(`${data.errors.length} line(s) had issues`);
        }
        setShowRosterDialog(false);
      } else {
        toast.error(data.detail || 'Failed to save roster');
      }
    } catch {
      toast.error('Failed to save roster');
    }
    setRosterSaving(false);
  };


  // Get courts for selected event
  const selectedEventData = filterEventId !== 'all' ? events.find(e => e.id === filterEventId) : null;
  const eventCourts = selectedEventData?.courts || [];

  // Format scheduled time helper
  const formatScheduledTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  };

  // Filter games
  const filteredGames = games.filter(game => {
    const matchesSearch = 
      game.home_team_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.away_team_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      game.title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || game.status === filterStatus;
    
    const matchesEvent = filterEventId === 'all' || game.event_id === filterEventId;
    
    const matchesCourt = filterCourtId === 'all' || 
      game.court_id === filterCourtId || 
      game.court_name === filterCourtId ||
      game.location === filterCourtId;
    
    return matchesSearch && matchesStatus && matchesEvent && matchesCourt;
  });

  // Sort all games chronologically - newest/soonest first
  const sortedGames = [...filteredGames].sort((a, b) => {
    const dateA = new Date(a.scheduled_time || a.scheduled_at || a.created_at || 0);
    const dateB = new Date(b.scheduled_time || b.scheduled_at || b.created_at || 0);
    return dateA - dateB; // Chronological (earliest first)
  });

  // Group games by Event
  const gamesByEvent = sortedGames.reduce((acc, game) => {
    const eventId = game.event_id || 'standalone';
    if (!acc[eventId]) {
      acc[eventId] = [];
    }
    acc[eventId].push(game);
    return acc;
  }, {});

  // Get event info for display
  const getEventInfo = (eventId) => {
    if (eventId === 'standalone') {
      return { name: 'Standalone Games', date: null };
    }
    const event = events.find(e => e.id === eventId);
    return event || { name: 'Unknown Event', date: null };
  };

  // Sort event groups by date (newest first), with standalone at the end
  const sortedEventIds = Object.keys(gamesByEvent).sort((a, b) => {
    if (a === 'standalone') return 1;
    if (b === 'standalone') return -1;
    const eventA = getEventInfo(a);
    const eventB = getEventInfo(b);
    return new Date(eventB.date || 0) - new Date(eventA.date || 0);
  });

  // Group games by status (for non-grouped view)
  const liveGames = sortedGames.filter(g => ['live', 'halftime', 'warmup'].includes(g.status));
  const scheduledGames = sortedGames.filter(g => g.status === 'scheduled');
  const completedGames = sortedGames.filter(g => g.status === 'final');

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  // Show login prompt if not authenticated (use isAuthenticated check, not raw token)
  if (!user && !loading && !authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-white mb-4">Operator Console</h1>
          <p className="text-zinc-400 mb-6">Please login as an operator to access the console</p>
          <Button onClick={() => navigate('/login', { state: { from: '/operator' } })} className="bg-blue-600 hover:bg-blue-500 text-white font-semibold h-12 px-6">
            Sign In
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-6" data-testid="operator-select">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <img 
            src="/rooam-logo.png" 
            alt="Rooam Sports" 
            className="h-12 w-auto"
          />
          <div>
            <h1 className="text-3xl font-bold text-white">Operator Console</h1>
            <p className="text-zinc-400 mt-1">Select a game to manage</p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-emerald-400 border-emerald-400 bg-emerald-950/50 px-3 py-1">
            <Users className="w-3 h-3 mr-1" />
            {user?.name || 'Operator'}
          </Badge>
          
          {/* Create Event Dialog */}
          <Dialog open={showCreateEventDialog} onOpenChange={setShowCreateEventDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-purple-500 text-purple-400 hover:bg-purple-500/10" data-testid="create-event-btn">
                <Calendar className="w-4 h-4 mr-2" /> New Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Event</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-300">Event Name *</Label>
                  <Input
                    value={eventForm.name}
                    onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                    placeholder="e.g., Summer Basketball Classic"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Sport *</Label>
                    <Select value={eventForm.sport} onValueChange={(v) => setEventForm({...eventForm, sport: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="basketball">Basketball</SelectItem>
                        <SelectItem value="soccer">Soccer</SelectItem>
                        <SelectItem value="volleyball">Volleyball</SelectItem>
                        <SelectItem value="football">Football</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Location *</Label>
                    <Input
                      value={eventForm.location}
                      onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                      placeholder="Sports Arena"
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Start Date *</Label>
                    <Input
                      type="date"
                      value={eventForm.date}
                      onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">End Date</Label>
                    <Input
                      type="date"
                      value={eventForm.end_date}
                      onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                </div>
                <Button onClick={createEvent} className="w-full bg-purple-600 hover:bg-purple-500 text-white">
                  Create Event
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Bulk Import Dialog */}
          <Dialog open={showBulkImportDialog} onOpenChange={(open) => {
            setShowBulkImportDialog(open);
            if (!open) { setBulkImportFile(null); setBulkImportEventId(''); setBulkImportResult(null); }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-green-500 text-green-400 hover:bg-green-500/10" data-testid="bulk-import-btn">
                <Upload className="w-4 h-4 mr-2" /> Bulk Import
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5 text-green-400" />
                  Bulk Import Games
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-white font-medium text-sm">Download Template</h4>
                      <p className="text-zinc-500 text-xs mt-1">CSV template with example data</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-zinc-600 text-zinc-300"
                      onClick={() => window.open(`${API_URL}/api/engine/games/template/csv`, '_blank')}
                    >
                      <Download className="h-4 w-4 mr-1" /> CSV
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Upload Spreadsheet *</Label>
                  <Input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => { setBulkImportFile(e.target.files?.[0] || null); setBulkImportResult(null); }}
                    className="mt-2 bg-zinc-800 border-zinc-600 text-white file:bg-zinc-700 file:text-white file:border-0 file:mr-2"
                  />
                  <p className="text-zinc-500 text-xs mt-1">Supported: CSV, Excel (.xlsx, .xls)</p>
                </div>
                <div>
                  <Label className="text-zinc-300">Assign to Event (optional)</Label>
                  <Select value={bulkImportEventId || "none"} onValueChange={(v) => setBulkImportEventId(v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                      <SelectValue placeholder="Select an event..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-600">
                      <SelectItem value="none">No Event (Standalone Games)</SelectItem>
                      {events.map(evt => (
                        <SelectItem key={evt.id} value={evt.id}>{evt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {bulkImportResult && (
                  <div className={`rounded-lg p-4 border ${bulkImportResult.success ? 'bg-green-500/10 border-green-500/50' : 'bg-red-500/10 border-red-500/50'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {bulkImportResult.success ? <CheckCircle2 className="h-5 w-5 text-green-400" /> : <XCircle className="h-5 w-5 text-red-400" />}
                      <span className={`font-medium ${bulkImportResult.success ? 'text-green-400' : 'text-red-400'}`}>
                        Import {bulkImportResult.success ? 'Complete' : 'Failed'}
                      </span>
                    </div>
                    <p className="text-sm text-zinc-300">✓ Imported: {bulkImportResult.imported} games</p>
                    {bulkImportResult.failed > 0 && <p className="text-sm text-red-400">✗ Failed: {bulkImportResult.failed} rows</p>}
                    {bulkImportResult.errors?.length > 0 && (
                      <div className="mt-2 text-xs text-red-400 max-h-24 overflow-y-auto">
                        {bulkImportResult.errors.map((err, i) => <p key={i}>{err}</p>)}
                      </div>
                    )}
                  </div>
                )}
                <Button onClick={handleBulkImport} disabled={!bulkImportFile || bulkImporting} className="w-full bg-green-600 hover:bg-green-500 text-white">
                  {bulkImporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing...</> : <><Upload className="h-4 w-4 mr-2" /> Import Games</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Create Game Dialog */}
          <Dialog open={showCreateGameDialog} onOpenChange={setShowCreateGameDialog}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold" data-testid="create-game-btn">
                <Plus className="w-4 h-4 mr-2" /> New Game
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white">Create New Game</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-300">Event (optional)</Label>
                  <Select value={gameForm.event_id || "none"} onValueChange={(v) => setGameForm({...gameForm, event_id: v === "none" ? "" : v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                      <SelectValue placeholder="Select an event..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-600">
                      <SelectItem value="none">No Event (Standalone Game)</SelectItem>
                      {events.map(evt => (
                        <SelectItem key={evt.id} value={evt.id}>{evt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-300">Game Title (optional)</Label>
                  <Input
                    value={gameForm.title}
                    onChange={(e) => setGameForm({...gameForm, title: e.target.value})}
                    placeholder="e.g., Championship Finals"
                    className="bg-zinc-800 border-zinc-600 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Home Team *</Label>
                    <Input
                      value={gameForm.home_team_name}
                      onChange={(e) => setGameForm({...gameForm, home_team_name: e.target.value})}
                      placeholder="Home Team"
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Away Team *</Label>
                    <Input
                      value={gameForm.away_team_name}
                      onChange={(e) => setGameForm({...gameForm, away_team_name: e.target.value})}
                      placeholder="Away Team"
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Sport</Label>
                    <Select value={gameForm.sport} onValueChange={(v) => setGameForm({...gameForm, sport: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-600">
                        <SelectItem value="basketball">Basketball</SelectItem>
                        <SelectItem value="soccer">Soccer</SelectItem>
                        <SelectItem value="volleyball">Volleyball</SelectItem>
                        <SelectItem value="football">Football</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-300">Location</Label>
                    <Input
                      value={gameForm.location}
                      onChange={(e) => setGameForm({...gameForm, location: e.target.value})}
                      placeholder="Venue/Court"
                      className="bg-zinc-800 border-zinc-600 text-white"
                    />
                  </div>
                </div>
                <Button onClick={createGame} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white">
                  Create Game
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Edit Game Dialog */}
      <Dialog open={showEditGameDialog} onOpenChange={(open) => { setShowEditGameDialog(open); if (!open) setEditingGame(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="h-5 w-5 text-blue-400" />
              Edit Game
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Event (optional)</Label>
              <Select value={gameForm.event_id || "none"} onValueChange={(v) => setGameForm({...gameForm, event_id: v === "none" ? "" : v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                  <SelectValue placeholder="Select an event..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-600">
                  <SelectItem value="none">No Event (Standalone Game)</SelectItem>
                  {events.map(evt => (
                    <SelectItem key={evt.id} value={evt.id}>{evt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-300">Game Title</Label>
              <Input
                value={gameForm.title}
                onChange={(e) => setGameForm({...gameForm, title: e.target.value})}
                placeholder="e.g., Championship Finals"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Home Team *</Label>
                <Input
                  value={gameForm.home_team_name}
                  onChange={(e) => setGameForm({...gameForm, home_team_name: e.target.value})}
                  placeholder="Home Team"
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Away Team *</Label>
                <Input
                  value={gameForm.away_team_name}
                  onChange={(e) => setGameForm({...gameForm, away_team_name: e.target.value})}
                  placeholder="Away Team"
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Sport</Label>
                <Select value={gameForm.sport} onValueChange={(v) => setGameForm({...gameForm, sport: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="soccer">Soccer</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                    <SelectItem value="football">Football</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Location</Label>
                <Input
                  value={gameForm.location}
                  onChange={(e) => setGameForm({...gameForm, location: e.target.value})}
                  placeholder="Venue/Court"
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
            </div>
            <Button onClick={updateGame} className="w-full bg-blue-600 hover:bg-blue-500 text-white">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Event Dialog */}
      <Dialog open={showEditEventDialog} onOpenChange={(open) => { setShowEditEventDialog(open); if (!open) setEditingEvent(null); }}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Edit className="h-5 w-5 text-purple-400" />
              Edit Event
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-300">Event Name *</Label>
              <Input
                value={eventForm.name}
                onChange={(e) => setEventForm({...eventForm, name: e.target.value})}
                placeholder="e.g., Summer Basketball Classic"
                className="bg-zinc-800 border-zinc-600 text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Sport *</Label>
                <Select value={eventForm.sport} onValueChange={(v) => setEventForm({...eventForm, sport: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-600">
                    <SelectItem value="basketball">Basketball</SelectItem>
                    <SelectItem value="soccer">Soccer</SelectItem>
                    <SelectItem value="volleyball">Volleyball</SelectItem>
                    <SelectItem value="football">Football</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">Location *</Label>
                <Input
                  value={eventForm.location}
                  onChange={(e) => setEventForm({...eventForm, location: e.target.value})}
                  placeholder="Sports Arena"
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Start Date *</Label>
                <Input
                  type="date"
                  value={eventForm.date}
                  onChange={(e) => setEventForm({...eventForm, date: e.target.value})}
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-300">End Date</Label>
                <Input
                  type="date"
                  value={eventForm.end_date}
                  onChange={(e) => setEventForm({...eventForm, end_date: e.target.value})}
                  className="bg-zinc-800 border-zinc-600 text-white"
                />
              </div>
            </div>
            <Button onClick={updateEvent} className="w-full bg-purple-600 hover:bg-purple-500 text-white">
              Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Roster Dialog */}
      <Dialog open={showRosterDialog} onOpenChange={setShowRosterDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Roster — {rosterTeamName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-zinc-400 text-xs">
              Enter one player per line. Format: <span className="text-cyan-400 font-mono">number name</span> or <span className="text-cyan-400 font-mono">name number</span>
            </p>
            <div className="bg-zinc-800 rounded-lg p-2 border border-zinc-700 text-[11px] text-zinc-500 font-mono space-y-0.5">
              <p>1 Marcus Johnson</p>
              <p>3 DeAndre Williams</p>
              <p>12 Tyler Brooks</p>
            </div>
            <textarea
              value={rosterText}
              onChange={(e) => setRosterText(e.target.value)}
              placeholder={"1 Marcus Johnson\n3 DeAndre Williams\n12 Tyler Brooks"}
              className="w-full h-48 bg-zinc-800 border border-zinc-700 rounded-lg p-3 text-white text-sm font-mono placeholder:text-zinc-600 focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
              data-testid="roster-textarea"
            />
            <div className="flex items-center justify-between">
              <p className="text-zinc-500 text-xs">
                {rosterText.trim() ? rosterText.trim().split('\n').filter(l => l.trim()).length : 0} players
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300" onClick={() => setShowRosterDialog(false)}>Cancel</Button>
                <Button size="sm" className="bg-cyan-600 hover:bg-cyan-500 text-white" onClick={saveRoster} disabled={rosterSaving} data-testid="save-roster-btn">
                  {rosterSaving ? 'Saving...' : 'Save Roster'}
                </Button>
              </div>
            </div>
            <p className="text-zinc-600 text-[10px]">Roster is saved to the team and will auto-load for future games.</p>
          </div>
        </DialogContent>
      </Dialog>


      {/* Search & Filter */}
      <div className="flex gap-3 mb-6 flex-wrap items-end">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search games..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-600 text-white placeholder:text-zinc-500 h-11"
            data-testid="search-input"
          />
        </div>

        {/* Event Filter */}
        <Select value={filterEventId} onValueChange={(v) => { setFilterEventId(v); setFilterCourtId('all'); }} modal={false}>
          <SelectTrigger className="bg-zinc-800 border-zinc-600 text-white w-[200px] h-11" data-testid="event-filter">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-600 z-[9999]">
            <SelectItem value="all">All Events</SelectItem>
            {events.map(evt => (
              <SelectItem key={evt.id} value={evt.id}>{evt.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Court Filter - only shows when an event with courts is selected */}
        {eventCourts.length > 0 && (
          <Select value={filterCourtId} onValueChange={setFilterCourtId} modal={false}>
            <SelectTrigger className="bg-zinc-800 border-amber-600/50 text-white w-[180px] h-11" data-testid="court-filter">
              <SelectValue placeholder="All Courts" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-600 z-[9999]">
              <SelectItem value="all">All Courts</SelectItem>
              {eventCourts.map(court => (
                <SelectItem key={court.id} value={court.name}>{court.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        
        <div className="flex gap-2">
          {['all', 'scheduled', 'live', 'final'].map(status => (
            <Button
              key={status}
              variant={filterStatus === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus(status)}
              className={filterStatus === status 
                ? 'bg-blue-600 text-white' 
                : 'border-zinc-600 text-zinc-300 hover:text-white hover:border-zinc-500 bg-zinc-800/50'
              }
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </Button>
          ))}
        </div>
      </div>

      {/* Games Grouped by Event/Tournament */}
      {filterEventId === 'all' ? (
        // Show games grouped by Event — compact rows
        sortedEventIds.map(eventId => {
          const eventGames = gamesByEvent[eventId];
          const eventInfo = getEventInfo(eventId);
          
          const sortedEventGames = [...eventGames].sort((a, b) => {
            const timeA = new Date(a.scheduled_time || a.scheduled_at || '9999-12-31');
            const timeB = new Date(b.scheduled_time || b.scheduled_at || '9999-12-31');
            return timeA - timeB;
          });
          
          const liveCount = sortedEventGames.filter(g => ['live', 'halftime', 'warmup'].includes(g.status)).length;
          
          return (
            <div key={eventId} className="mb-8">
              {/* Event Header */}
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-700">
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-8 rounded-full ${eventId === 'standalone' ? 'bg-zinc-600' : 'bg-purple-500'}`} />
                  <div>
                    <h2 className="text-lg font-bold text-white">{eventInfo.name}</h2>
                    {eventInfo.date && (
                      <p className="text-xs text-zinc-400">
                        {new Date(eventInfo.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {eventInfo.location && ` • ${eventInfo.location}`}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  {liveCount > 0 && <Badge className="bg-red-600 text-white text-[10px]">{liveCount} LIVE</Badge>}
                  <span>{eventGames.length} games</span>
                </div>
              </div>

              {/* Compact game rows */}
              <div className="space-y-1">
                {sortedEventGames.map(game => {
                  const sched = formatScheduledTime(game.scheduled_time || game.scheduled_at);
                  const isLive = ['live','halftime','warmup'].includes(game.status);
                  const isFinal = game.status === 'final';
                  return (
                    <div 
                      key={game.game_id}
                      onClick={() => navigate(`/operator/game/${game.game_id}`)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all hover:bg-zinc-800 ${
                        isLive ? 'bg-red-500/10 border border-red-500/30' : 'bg-zinc-900/50 border border-zinc-800'
                      }`}
                    >
                      {/* Time */}
                      <div className="w-16 shrink-0 text-right">
                        {sched ? (
                          <span className={`text-sm font-semibold ${isLive ? 'text-red-400' : isFinal ? 'text-zinc-500' : 'text-amber-400'}`}>
                            {sched.time}
                          </span>
                        ) : (
                          <span className="text-xs text-zinc-600">--:--</span>
                        )}
                      </div>
                      
                      {/* Status dot */}
                      <div className={`w-2 h-2 rounded-full shrink-0 ${
                        isLive ? 'bg-red-500 animate-pulse' : isFinal ? 'bg-emerald-500' : 'bg-zinc-600'
                      }`} />
                      
                      {/* Teams */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold truncate ${isFinal ? 'text-zinc-400' : 'text-white'}`}>
                          {game.home_team_name} <span className="text-zinc-600 font-normal">vs</span> {game.away_team_name}
                        </p>
                        <p className="text-[10px] text-zinc-600 truncate">
                          {[game.division, game.location].filter(Boolean).join(' • ')}
                        </p>
                      </div>
                      
                      {/* Score */}
                      {(isLive || isFinal) && (
                        <span className={`text-sm font-bold shrink-0 ${isLive ? 'text-white' : 'text-zinc-400'}`}>
                          {game.score?.home || 0} - {game.score?.away || 0}
                        </span>
                      )}

                      {/* Roster buttons */}
                      <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => openRosterDialog(game.home_team_name, 'home', game.game_id)}
                          className="p-1.5 rounded bg-zinc-800 hover:bg-cyan-600/20 border border-zinc-700 hover:border-cyan-600 transition-colors group"
                          title={`${game.home_team_name} roster`}
                        >
                          <Users className="w-3 h-3 text-zinc-500 group-hover:text-cyan-400" />
                        </button>
                        <button
                          onClick={() => openRosterDialog(game.away_team_name, 'away', game.game_id)}
                          className="p-1.5 rounded bg-zinc-800 hover:bg-cyan-600/20 border border-zinc-700 hover:border-cyan-600 transition-colors group"
                          title={`${game.away_team_name} roster`}
                        >
                          <Users className="w-3 h-3 text-zinc-500 group-hover:text-cyan-400" />
                        </button>
                      </div>
                      
                      {/* Status */}
                      <Badge className={`${statusColors[game.status] || 'bg-zinc-600'} text-white text-[10px] px-2 py-0.5 shrink-0`}>
                        {game.status?.toUpperCase()}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      ) : (
        // When filtered to specific event - organize by COURT
        (() => {
          // Group games by court/location
          const gamesByCourt = {};
          filteredGames.forEach(game => {
            const court = game.location || game.court_name || 'Unassigned';
            if (!gamesByCourt[court]) gamesByCourt[court] = [];
            gamesByCourt[court].push(game);
          });
          // Sort courts naturally
          const sortedCourts = Object.keys(gamesByCourt).sort((a, b) => {
            const numA = parseInt(a.replace(/\D/g, '')) || 999;
            const numB = parseInt(b.replace(/\D/g, '')) || 999;
            return numA - numB;
          });

          return (
            <>
              {/* Court tabs */}
              <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
                {sortedCourts.map(court => {
                  const courtGames = gamesByCourt[court];
                  const liveCount = courtGames.filter(g => ['live','halftime','warmup'].includes(g.status)).length;
                  return (
                    <button
                      key={court}
                      onClick={() => setFilterCourtId(filterCourtId === court ? 'all' : court)}
                      className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                        filterCourtId === court 
                          ? 'bg-amber-500 text-black' 
                          : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700 border border-zinc-700'
                      }`}
                    >
                      {court} ({courtGames.length}){liveCount > 0 ? ` • ${liveCount} LIVE` : ''}
                    </button>
                  );
                })}
              </div>

              {/* Games listed by court */}
              {sortedCourts
                .filter(court => filterCourtId === 'all' || court === filterCourtId)
                .map(court => {
                const courtGames = gamesByCourt[court].sort((a, b) => {
                  const tA = new Date(a.scheduled_time || a.scheduled_at || '9999-12-31');
                  const tB = new Date(b.scheduled_time || b.scheduled_at || '9999-12-31');
                  return tA - tB;
                });

                return (
                  <div key={court} className="mb-8">
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b border-zinc-700/50">
                      <MapPin className="w-4 h-4 text-amber-500" />
                      <h3 className="text-lg font-bold text-white">{court}</h3>
                      <span className="text-xs text-zinc-500">{courtGames.length} games</span>
                    </div>
                    
                    {/* Compact row-based layout */}
                    <div className="space-y-1">
                      {courtGames.map(game => {
                        const sched = formatScheduledTime(game.scheduled_time || game.scheduled_at);
                        const isLive = ['live','halftime','warmup'].includes(game.status);
                        const isFinal = game.status === 'final';
                        return (
                          <div 
                            key={game.game_id}
                            onClick={() => navigate(`/operator/game/${game.game_id}`)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all hover:bg-zinc-800 ${
                              isLive ? 'bg-red-500/10 border border-red-500/30' : 'bg-zinc-900/50 border border-zinc-800'
                            }`}
                            data-testid={`operator-game-row-${game.game_id}`}
                          >
                            {/* Time */}
                            <div className="w-16 shrink-0 text-right">
                              {sched ? (
                                <span className={`text-sm font-semibold ${isLive ? 'text-red-400' : isFinal ? 'text-zinc-500' : 'text-amber-400'}`}>
                                  {sched.time}
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-600">--:--</span>
                              )}
                            </div>
                            
                            {/* Status dot */}
                            <div className={`w-2 h-2 rounded-full shrink-0 ${
                              isLive ? 'bg-red-500 animate-pulse' : isFinal ? 'bg-emerald-500' : 'bg-zinc-600'
                            }`} />
                            
                            {/* Teams */}
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-semibold truncate ${isFinal ? 'text-zinc-400' : 'text-white'}`}>
                                {game.home_team_name} <span className="text-zinc-600 font-normal">vs</span> {game.away_team_name}
                              </p>
                              {game.division && (
                                <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{game.division}</p>
                              )}
                            </div>
                            
                            {/* Score (if live/final) */}
                            {(isLive || isFinal) && (
                              <div className="shrink-0 text-right">
                                <span className={`text-sm font-bold ${isLive ? 'text-white' : 'text-zinc-400'}`}>
                                  {game.score?.home || 0} - {game.score?.away || 0}
                                </span>
                              </div>
                            )}

                            {/* Roster buttons */}
                            <div className="flex gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => openRosterDialog(game.home_team_name, 'home', game.game_id)}
                                className="p-1.5 rounded bg-zinc-800 hover:bg-cyan-600/20 border border-zinc-700 hover:border-cyan-600 transition-colors group"
                                title={`${game.home_team_name} roster`}
                              >
                                <Users className="w-3 h-3 text-zinc-500 group-hover:text-cyan-400" />
                              </button>
                              <button
                                onClick={() => openRosterDialog(game.away_team_name, 'away', game.game_id)}
                                className="p-1.5 rounded bg-zinc-800 hover:bg-cyan-600/20 border border-zinc-700 hover:border-cyan-600 transition-colors group"
                                title={`${game.away_team_name} roster`}
                              >
                                <Users className="w-3 h-3 text-zinc-500 group-hover:text-cyan-400" />
                              </button>
                            </div>
                            
                            {/* Status badge */}
                            <Badge className={`${statusColors[game.status] || 'bg-zinc-600'} text-white text-[10px] px-2 py-0.5 shrink-0`}>
                              {game.status?.toUpperCase()}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()
      )}

      {/* Empty State */}
      {filteredGames.length === 0 && (
        <div className="text-center py-12">
          <p className="text-zinc-400 mb-4 text-lg">No games found</p>
          <Button onClick={() => setShowCreateGameDialog(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-12 px-6">
            <Plus className="w-4 h-4 mr-2" /> Create First Game
          </Button>
        </div>
      )}
    </div>
  );
}

// Game Card Component
function GameCard({ game, onClick, onEdit, onDelete, onResetStatus }) {
  const formatTime = (ms) => {
    if (!ms && ms !== 0) return '--:--';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format scheduled time prominently
  const formatScheduledTime = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return {
      time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    };
  };

  const scheduledDisplay = formatScheduledTime(game.scheduled_time || game.scheduled_at);
  const canReset = ['live', 'halftime', 'warmup', 'final'].includes(game.status);

  return (
    <Card 
      className="bg-zinc-900 border-2 border-zinc-700 hover:border-zinc-500 transition-all shadow-lg"
      data-testid={`game-card-${game.game_id}`}
    >
      <CardContent className="p-4">
        {/* PROMINENT Scheduled Time Banner */}
        <div className={`-mx-4 -mt-4 mb-4 px-4 py-2 flex items-center justify-between ${
          scheduledDisplay ? 'bg-amber-500/20 border-b border-amber-500/30' : 'bg-zinc-800 border-b border-zinc-700'
        }`}>
          <div className="flex items-center gap-2">
            <Clock className={`w-4 h-4 ${scheduledDisplay ? 'text-amber-400' : 'text-zinc-500'}`} />
            {scheduledDisplay ? (
              <span className="font-bold text-amber-400">{scheduledDisplay.time}</span>
            ) : (
              <span className="text-zinc-500 text-sm">No time set</span>
            )}
          </div>
          {scheduledDisplay && (
            <span className="text-amber-400/70 text-sm">{scheduledDisplay.date}</span>
          )}
        </div>

        {/* Status Badge & Actions */}
        <div className="flex justify-between items-start mb-4">
          <Badge className={`${statusColors[game.status] || 'bg-zinc-600'} text-white font-semibold px-3 py-1`}>
            {game.status?.toUpperCase()}
          </Badge>
          <div className="flex items-center gap-1">
            {game.is_streaming && (
              <Badge variant="outline" className="text-red-400 border-red-400 bg-red-950/30 mr-1">
                <Radio className="w-3 h-3 mr-1" /> LIVE
              </Badge>
            )}
            {canReset && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-zinc-400 hover:text-amber-400 hover:bg-amber-400/10"
                onClick={(e) => { e.stopPropagation(); onResetStatus?.(game.game_id, 'scheduled'); }}
                title="Reset to Scheduled"
                data-testid={`reset-game-${game.game_id}`}
              >
                <Undo2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-zinc-400 hover:text-blue-400 hover:bg-blue-400/10"
              onClick={(e) => { e.stopPropagation(); onEdit?.(game); }}
              data-testid={`edit-game-${game.game_id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-zinc-400 hover:text-red-400 hover:bg-red-400/10"
              onClick={(e) => { e.stopPropagation(); onDelete?.(game.game_id, game.title || `${game.home_team_name} vs ${game.away_team_name}`); }}
              data-testid={`delete-game-${game.game_id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Teams & Score */}
        <div className="space-y-3 mb-4 cursor-pointer" onClick={onClick}>
          <div className="flex justify-between items-center">
            <span className="font-semibold text-white text-lg truncate flex-1">{game.home_team_name}</span>
            <span className="text-3xl font-bold text-white ml-3 tabular-nums">{game.score?.home || 0}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="font-semibold text-white text-lg truncate flex-1">{game.away_team_name}</span>
            <span className="text-3xl font-bold text-white ml-3 tabular-nums">{game.score?.away || 0}</span>
          </div>
        </div>

        {/* Clock & Period */}
        <div className="flex justify-between items-center text-base bg-zinc-800 rounded-lg px-3 py-2 border border-zinc-700">
          <span className="text-zinc-300 font-medium">Period {game.clock?.period || 1}</span>
          <span className="font-mono text-white font-semibold">{formatTime(game.clock?.time_remaining_ms)}</span>
        </div>

        {/* Court Info */}
        {game.court_id && (
          <div className="mt-3 flex items-center text-sm text-zinc-400">
            <MapPin className="w-4 h-4 mr-2" />
            Court: {game.court_name || game.court_id}
          </div>
        )}

        {/* Venue/Event */}
        {(game.venue_name || game.event_name) && (
          <div className="mt-2 text-sm text-zinc-400 flex items-center">
            <Calendar className="w-4 h-4 mr-2" />
            {game.venue_name || game.event_name}
          </div>
        )}

        {/* Enter Button */}
        <div className="mt-4 pt-3 border-t border-zinc-700">
          <Button 
            variant="ghost" 
            className="w-full text-cyan-400 hover:text-cyan-300 hover:bg-zinc-800 font-semibold text-base h-11"
            onClick={onClick}
          >
            Open Console <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
